import { faEye, faEyeSlash, faLock } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AlertTitle, Box, Button, IconButton, InputAdornment, styled, Table, TableBody, TableCell, TableRow, TextField, TextFieldProps, Typography } from '@mui/material';
import { grey } from "@mui/material/colors";
import { Dispatch, FC, HTMLInputTypeAttribute, ReactNode, SetStateAction, useCallback, useContext, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useParams } from "react-router-dom";
import { SendDataAccessRequestEmail } from "../api/dtos/dtos";
import Center from "../components/common/Center";
import Hr from "../components/common/HorizontalRule";
import { UserFormatError } from "../constants/exceptions/CustomExceptions";
import { ERROR_TIMEOUT, SUCCESS_TIMEOUT } from "../constants/styles/AlertConstants";
import AuthContext from "../contexts/auth/AuthContext";
import { SpeciesContext } from "../contexts/data/SpeciesContext";
import AlertContext from "../contexts/styles/AlertContext";
import useServiceStackClient from "../hooks/api/useServiceStackClient";

interface IStyledTableRowProps {
    primaryLabel: string;
    secondaryLabel?: string;

    type?: HTMLInputTypeAttribute;

    fieldState?: FieldState;

    select?: boolean;
    items?: ReactNode[];
    
    disabled?: boolean;

    value: any;
    onChange?: Dispatch<SetStateAction<any>>
}

type FieldState = {
    state?: "error" | "warning";
    message?: string;
}

const StyledTableCell = styled(TableCell)(() => ({
    padding: '8px',
    border: 'none'
}));

const StyledTextField = styled(TextField, { shouldForwardProp: (props) => props !== 'stateColor' })<TextFieldProps & { stateColor?: string }>(({ theme, stateColor, disabled }) => ({
    '& label': {
        color: stateColor === 'error' ?
                theme.palette.error.main :
            stateColor === 'warning' ?
                theme.palette.warning.main :
                grey[700],
    },
    '& p': {
        color: stateColor === 'error' ?
                theme.palette.error.main :
            stateColor === 'warning' ?
                theme.palette.warning.main :
                grey[700]
    },
    '& .MuiOutlinedInput-root': {
        '& fieldset': {
            borderColor: stateColor === 'error' ? 
                    `${theme.palette.error.main}77` :
                stateColor === 'warning' ?
                    theme.palette.warning.main :
                    'rgba(0, 0, 0, 0.2)',
        },
        '&:hover fieldset': {
            borderColor: stateColor === 'error' ? 
                    theme.palette.error.main :
                stateColor === 'warning' ?
                    theme.palette.warning.main :
                disabled ?
                    'rgba(0, 0, 0, 0.2)' :
                    'rgb(0, 0, 0)',
        },
        '&.Mui-focused fieldset': {
            borderColor: stateColor === 'error' ? 
                    theme.palette.error.main :
                stateColor === 'warning' ?
                    theme.palette.warning.main :
                    theme.palette.primary.main
        },
    },
}));

const Container = styled(Box)(() => ({
    padding: '30px 14px 8px 14px'
}));

const StyledConfirmButton = styled(Button)(({ theme }) => ({
    maxWidth: '100px',
    minWidth: '100px',
    minHeight: '40px',
    maxHeight: '40px',
    backgroundColor: theme.palette.primary.main,
    color: grey[100],
    marginLeft: '30px',

    "&:hover": {
        backgroundColor: theme.palette.primary.main,
        boxShadow: '0px 0px 6px 1px rgba(75,90,35,0.5)'
    },

    "&:active": {
        backgroundColor: theme.palette.secondary.main
    }
}));

const StyledTableRow: FC<IStyledTableRowProps> = ({
    primaryLabel, 
    secondaryLabel,
    type,
    select,
    items,
    fieldState,
    disabled,
    value,
    onChange
}) => {
    const [showPassword, setShowPassword] = useState<boolean>(false);

    return (
        <TableRow>
            <StyledTableCell sx={{ minWidth: '124px', maxWidth: '124px' }}>
                {primaryLabel}:
            </StyledTableCell>
            <StyledTableCell width='100%'>
                {
                    select ? 
                        <StyledTextField
                            select
                            stateColor={fieldState?.state}
                            color={fieldState?.state}
                            helperText={fieldState?.message}
                            disabled={!!disabled}
                            label={secondaryLabel ?? ""}
                            fullWidth
                            size='small'
                            value={value}
                            onChange={onChange}
                            InputLabelProps={{
                                shrink: true,
                            }}
                        >
                            {items}
                        </StyledTextField> :
                        <StyledTextField
                            id={`Text-Field-${primaryLabel.replaceAll(' ', '-')}`}
                            inputProps={{
                                autoComplete: 'password',
                            }}
                            InputProps={{
                                endAdornment: type === 'password' ? (
                                    <InputAdornment position="end">
                                        <IconButton 
                                            disableRipple
                                            sx={{ width: '24px', height: '24px' }}    
                                            onMouseDown={() => setShowPassword(true)} 
                                            onMouseUp={() => setShowPassword(false)} 
                                            onMouseLeave={() => setShowPassword(false)}
                                        >
                                            {
                                                showPassword ? 
                                                    <FontAwesomeIcon icon={faEye} style={{ fontSize: '1.2rem' }} /> : 
                                                    <FontAwesomeIcon icon={faEyeSlash} style={{ fontSize: '1.2rem' }} />
                                            }
                                        </IconButton>
                                    </InputAdornment>
                                ) :
                                disabled ? (
                                    <InputAdornment position="end">
                                        <FontAwesomeIcon icon={faLock} style={{ fontSize: '1.2rem' }}/>
                                    </InputAdornment>
                                ) : 
                                null
                            }}
                            autoComplete='password'
                            type={type === 'password' ? !showPassword ? 'password' : 'text' : type}
                            stateColor={fieldState?.state}
                            color={fieldState?.state}
                            helperText={fieldState?.message}
                            disabled={!!disabled}
                            label={secondaryLabel ?? ""}
                            fullWidth
                            size='small'
                            value={value}
                            onChange={onChange}
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />
                }
            </StyledTableCell>
        </TableRow>
    )
}

const RequestDataAccessPage: FC = () => {

    //
    // -- Refs --
    const recaptchaRef = useRef<ReCAPTCHA | null>(null);

    //
    // -- Hooks --
    const client = useServiceStackClient();

    // -- Context --
    const { user } = useContext(AuthContext);
    const { addAlert } = useContext(AlertContext);
    const { species: { species } } = useContext(SpeciesContext);
    const { species: speciesName } = useParams();

    // -- Data Access Request state --
    const [dataAccessRequestDetails, setDataAccessRequestDetails] = useState<SendDataAccessRequestEmail | null>(user?.fullName && user?.email ? new SendDataAccessRequestEmail({fullName: user.fullName, emailAddress: user.email}) : null);
    const [dataAccessRequestDetailsState, setDataAccessRequestDetailsState] = useState<Record<string, FieldState>>({});
    const [disabled, setDisabled] = useState<boolean>(false);
    const [submitted, setSubmitted] = useState<boolean>(false);

    // Validate the data access request fields
    const validateDataAccessRequestField = useCallback((field: string, value: any, warn?: boolean): boolean => {
        // Confirm the field is a key of SendDataAccessRequestEmail type
        if (field as keyof SendDataAccessRequestEmail) {
            // Update form full name
            if (field === 'fullName') {
                const valid = value && value !== ''

                if (!valid) {
                    setDataAccessRequestDetailsState(c => {
                        return {
                            ...c,
                            [field]: {
                                state: 'error',
                                message: 'Full Name is required.'
                            }
                        };
                    });
                } 
                else {
                    setDataAccessRequestDetailsState(c => {
                        delete c[field];
                        return c;
                    })
                }

                return valid;
            }
            else if (field === 'organisationName') {
                const valid = value && value !== ''

                if (!valid) {
                    setDataAccessRequestDetailsState(c => {
                        return {
                            ...c,
                            [field]: {
                                state: 'error',
                                message: 'Organisation name is required.'
                            }
                        };
                    });
                } 
                else {
                    setDataAccessRequestDetailsState(c => {
                        delete c[field];
                        return c;
                    })
                }

                return valid;
            }
            else if (field === 'details') {
                const valid = value && value !== ''

                if (!valid) {
                    setDataAccessRequestDetailsState(c => {
                        return {
                            ...c,
                            [field]: {
                                state: 'error',
                                message: 'Details are required.'
                            }
                        };
                    });
                } 
                else {
                    setDataAccessRequestDetailsState(c => {
                        delete c[field];
                        return c;
                    })
                }

                return valid;
            }
            // Update email
            else if (field === 'emailAddress') {
                const regex = /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/

                const valid = regex.test(value);
                const errorMessage = !value || value === '' ?
                    'Email is required.' :
                    'Invalid email.'

                if (!valid) {
                    if (warn) {
                        setDataAccessRequestDetailsState(c => {
                            return {
                                ...c,
                                [field]: {
                                    state: 'warning',
                                    message: 'Invalid email'
                                }
                            };
                        });
                    }
                    else {
                        setDataAccessRequestDetailsState(c => {
                            return {
                                ...c,
                                field: {
                                    state: 'error',
                                    message: errorMessage
                                }
                            };
                        });
                    }
                }
                else {
                    setDataAccessRequestDetailsState(c => {
                        delete c[field];
                        return c;
                    })
                }

                return valid;
            }
            // Otherwise, clear all errors/warnings
            else {
                setDataAccessRequestDetailsState({});
            }

            return true;
        }

        return false;

    }, [setDataAccessRequestDetailsState]);

    const handleDataAccessRequestChange = useCallback((field: string, value: any) => {
        validateDataAccessRequestField(field, value, true);

        setDataAccessRequestDetails(c => {
            return new SendDataAccessRequestEmail({
                ...c,
                [field]: value
            });
        })
    }, [validateDataAccessRequestField, setDataAccessRequestDetails]);

    const handleDataAccessRequestSubmit = useCallback(async () => {
        if (disabled) {
            return;
        }
        setDisabled(true);

        if (
            validateDataAccessRequestField('fullName', dataAccessRequestDetails?.fullName) &&
            validateDataAccessRequestField('emailAddress', dataAccessRequestDetails?.emailAddress) && 
            validateDataAccessRequestField('organisationName', dataAccessRequestDetails?.organisationName) && 
            validateDataAccessRequestField('details', dataAccessRequestDetails?.details) && 
            recaptchaRef?.current?.getValue() !== ""
        ) {
            try {
                // Make registration request
                await client.post(
                    new SendDataAccessRequestEmail({
                        species: species,
                        fullName: dataAccessRequestDetails?.fullName,
                        emailAddress: dataAccessRequestDetails?.emailAddress,
                        organisationName: dataAccessRequestDetails?.organisationName,
                        details: dataAccessRequestDetails?.details,
                    })
                );
                
                // Alert user of success
                addAlert({
                    content: <AlertTitle>
                            Data access request sent successfully.
                        </AlertTitle>,
                    dismissable: true,
                    severity: 'success',
                    timeout: SUCCESS_TIMEOUT
                });

                recaptchaRef.current?.reset();
                setSubmitted(true);
                return;
            } catch (error: any) {

                recaptchaRef.current?.reset();

                if (error?.responseStatus) {
                    if (error.responseStatus.errorCode === UserFormatError.errorCode) {
                        let newState: Record<string, FieldState> = {};

                        for (var key of Object.keys(error.responseStatus.meta)) {
                            newState[key] = {
                                message: error.responseStatus.meta[key],
                                state: 'error'
                            }
                        }

                        addAlert({
                            content: <AlertTitle>
                                    {newState[Object.keys(newState)[0]].message}
                                </AlertTitle>,
                            dismissable: true,
                            severity: 'error',
                            timeout: ERROR_TIMEOUT
                        });

                        setDataAccessRequestDetailsState(newState);
                    }
                    else {

                        recaptchaRef.current?.reset();

                        addAlert({
                            content: <AlertTitle>
                                    {error.responseStatus.message}
                                </AlertTitle>,
                            dismissable: true,
                            severity: 'error',
                            timeout: ERROR_TIMEOUT
                        });
                    }
                }
                else {

                    recaptchaRef.current?.reset();

                    addAlert({
                        content: <AlertTitle>
                                {error.message}
                            </AlertTitle>,
                        dismissable: true,
                        severity: 'error',
                        timeout: ERROR_TIMEOUT
                    });
                }
            }
        }
        else {
            addAlert({
                content: <AlertTitle>
                        Please enter all required fields.
                    </AlertTitle>,
                dismissable: true,
                severity: 'error',
                timeout: ERROR_TIMEOUT
            });
        }
        setDisabled(false);
    }, [
        client, 
        dataAccessRequestDetails, 
        validateDataAccessRequestField, 
        addAlert, 
        setDataAccessRequestDetailsState, 
        setDisabled, 
        disabled,
        setSubmitted
    ]);
    
    return (
        <Container>
            <Typography color="#333" variant='h2'>Request Data Access for {speciesName?.replace("scan", "")?.replace("alert", "").capitaliseFirstLetter()}</Typography>
            <Hr color={grey[400]} sx={{ margin: '40px 0' }} />
            {
                submitted ?
                    <Box sx={{ height: '50vh' }}>
                        <Typography color="#333" variant='h6'>Your request has been submitted. Thank you for your request.</Typography>
                    </Box> :
                    <>
                        <Table>
                            <TableBody>
                                <StyledTableRow
                                    primaryLabel="Full Name"
                                    fieldState={dataAccessRequestDetailsState['fullName']}
                                    value={dataAccessRequestDetails?.fullName}
                                    onChange={(v) => handleDataAccessRequestChange('fullName', v.target.value)}
                                />
                                <StyledTableRow
                                    primaryLabel="Email"
                                    fieldState={dataAccessRequestDetailsState['emailAddress']}
                                    value={dataAccessRequestDetails?.emailAddress}
                                    onChange={(v) => handleDataAccessRequestChange('emailAddress', v.target.value)}
                                />
                                <StyledTableRow
                                    primaryLabel="Organisation Name"
                                    fieldState={dataAccessRequestDetailsState['organisationName']}
                                    value={dataAccessRequestDetails?.organisationName}
                                    onChange={(v) => handleDataAccessRequestChange('organisationName', v.target.value)}
                                />
                                <StyledTableRow
                                    primaryLabel="Details of this request"
                                    fieldState={dataAccessRequestDetailsState['details']}
                                    value={dataAccessRequestDetails?.details}
                                    onChange={(v) => handleDataAccessRequestChange('details', v.target.value)}
                                />
                            </TableBody>
                        </Table>

                        <Box
                            sx={{
                                pt: '5px',
                                pl: '130px'
                            }}
                        >
                            <ReCAPTCHA
                                ref={recaptchaRef}
                                sitekey="6LebIOUjAAAAAJBe-lms4_lrqq6yl2wU8wX8ZDTo"
                                onChange={() => {null}}
                            />
                        </Box>

                        <Center ha='end' m='15px 10px'>
                            <StyledConfirmButton
                                onClick={handleDataAccessRequestSubmit}
                                disabled={disabled}
                            >
                                <Typography fontWeight='600'>Submit</Typography>
                            </StyledConfirmButton>
                        </Center>
                    </>
            }
        </Container>
    )
}
export default RequestDataAccessPage
